import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertTriangle, Package, Calendar, CheckCircle, Clock, Bell, Star, Zap, IndianRupee, Trash2, Check } from 'lucide-react';

const initialNotifications = [
  { id: 1, type: 'overdue', read: false, title: 'Order ORD-003 is overdue',
    body: 'Anita Patel\'s Blouse was due 16 Feb. Complete immediately.',
    time: '2 hours ago', icon: AlertTriangle, color: '#991B1B', priority: 'high' },
  { id: 2, type: 'new-order', read: false, title: 'New urgent order',
    body: 'Priya Sharma — Suit Salwar (Urgent). Delivery by 20 Feb. ₹2,499.',
    time: '5 hours ago', icon: Zap, color: '#991B1B', priority: 'high' },
  { id: 3, type: 'new-order', read: false, title: 'New order received',
    body: 'Meena Kumari — Lehenga (Flexible). Delivery by 5 Mar. ₹4,999.',
    time: '6 hours ago', icon: Package, color: '#D4AF37', priority: 'medium' },
  { id: 4, type: 'counter', read: false, title: 'Counter offer pending',
    body: 'Suman Devi is reviewing your proposed date of 26 Feb for Kurti.',
    time: '8 hours ago', icon: Calendar, color: '#D4AF37', priority: 'medium' },
  { id: 5, type: 'deadline', read: true, title: 'Deadline approaching',
    body: 'ORD-002 (Rahul Verma) Formal Shirt due in 3 days.',
    time: '1 day ago', icon: Clock, color: '#D4AF37', priority: 'medium' },
  { id: 6, type: 'completed', read: true, title: 'Order delivered',
    body: 'ORD-008 Deepak Joshi — Kurta Pajama delivered successfully.',
    time: '2 days ago', icon: CheckCircle, color: '#166534', priority: 'low' },
  { id: 7, type: 'rating', read: true, title: '5-star review!',
    body: 'Neha Gupta: "Amazing stitching quality. Perfect fit!"',
    time: '3 days ago', icon: Star, color: '#D4AF37', priority: 'low' },
  { id: 8, type: 'earnings', read: true, title: 'Weekly earnings: ₹12,650',
    body: '8 orders completed this week. Up 15% from last week.',
    time: '4 days ago', icon: IndianRupee, color: '#0F0F0F', priority: 'low' },
  { id: 9, type: 'staff', read: true, title: 'Staff update',
    body: 'Raju Kumar completed 3 orders today. Top performer!',
    time: '4 days ago', icon: Check, color: '#166534', priority: 'low' },
];

const TailorNotifications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(initialNotifications);
  const [filter, setFilter] = useState('all');

  const unreadCount = items.filter(n => !n.read).length;
  const filtered = filter === 'unread' ? items.filter(n => !n.read) : filter === 'urgent' ? items.filter(n => n.priority === 'high') : items;

  const markAllRead = () => setItems(p => p.map(n => ({ ...n, read: true })));
  const markRead = (id) => setItems(p => p.map(n => n.id === id ? { ...n, read: true } : n));
  const deleteNotif = (id) => setItems(p => p.filter(n => n.id !== id));

  return (
    <div data-testid="tailor-notifications" className="min-h-screen"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>

      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-3 py-3 border-b bg-white" style={{ borderColor: '#E5E5E5' }}>
        <div className="flex items-center gap-2">
          <button data-testid="back-btn" onClick={() => navigate('/tailor-dashboard')} className="p-1">
            <ChevronLeft className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="text-base" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Notifications</h1>
            {unreadCount > 0 && <p className="text-[10px]" style={{ color: '#991B1B' }}>{unreadCount} unread</p>}
          </div>
        </div>
        {unreadCount > 0 && (
          <button data-testid="mark-all-read-btn" onClick={markAllRead} className="text-[11px]" style={{ color: '#D4AF37' }}>
            Mark all read
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="px-3 pt-3 pb-1.5 flex gap-1.5 overflow-x-auto scrollbar-hide">
        {[
          { id: 'all', label: `All (${items.length})` },
          { id: 'unread', label: `Unread (${unreadCount})` },
          { id: 'urgent', label: `Urgent (${items.filter(n => n.priority === 'high').length})` },
        ].map(f => (
          <button key={f.id} data-testid={`filter-${f.id}-btn`} onClick={() => setFilter(f.id)}
            className="flex-shrink-0 px-3 py-1.5 text-[10px] tracking-[0.05em] uppercase transition-colors"
            style={{
              backgroundColor: filter === f.id ? '#0F0F0F' : '#FFF',
              color: filter === f.id ? '#FFF' : '#595959',
              border: `1px solid ${filter === f.id ? '#0F0F0F' : '#E5E5E5'}`
            }}>{f.label}</button>
        ))}
      </div>

      {/* List */}
      <div className="px-3 py-2">
        {filtered.length > 0 ? (
          <div className="space-y-1.5">
            {filtered.map(n => {
              const Icon = n.icon;
              return (
                <div key={n.id} data-testid={`notif-${n.id}`}
                  className="bg-white border flex items-start gap-2.5 p-3 transition-colors animate-fadeIn"
                  style={{
                    borderColor: !n.read ? n.color + '30' : '#E5E5E5',
                    borderLeftWidth: 3,
                    borderLeftColor: !n.read ? n.color : '#E5E5E5'
                  }}>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: n.color + '12' }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: n.color }} strokeWidth={1.5} />
                  </div>
                  <button className="flex-1 min-w-0 text-left" onClick={() => markRead(n.id)}>
                    <div className="flex items-start gap-1.5">
                      <p className="text-[12px] flex-1" style={{ color: '#0F0F0F', fontWeight: n.read ? 400 : 500 }}>{n.title}</p>
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: n.color }} />}
                    </div>
                    <p className="text-[11px] mt-0.5 line-clamp-2" style={{ color: '#595959' }}>{n.body}</p>
                    <p className="text-[9px] mt-1.5" style={{ color: '#BFBFBF' }}>{n.time}</p>
                  </button>
                  <button data-testid={`delete-notif-${n.id}`} onClick={() => deleteNotif(n.id)} className="p-1 flex-shrink-0 mt-0.5">
                    <Trash2 className="w-3 h-3" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-14 text-center">
            <Bell className="w-7 h-7 mx-auto mb-2" style={{ color: '#E5E5E5' }} strokeWidth={1.5} />
            <p className="text-[12px]" style={{ color: '#595959' }}>No notifications</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default TailorNotifications;
